import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import produce from 'immer';
import * as EmbedFrame from 'marketplace-embed/constants/EmbedFrame';
import * as EmbedGroup from 'marketplace-embed/constants/EmbedGroup';
import * as PostMessageType from 'marketplace-embed/constants/PostMessageType';
import * as AssetBusinessType from 'marketplace-ui-assets-core/constants/AssetBusinessType';
import * as AssetPageFeature from 'marketplace-ui-assets-core/constants/AssetPageFeature';
import * as AssetPageType from 'marketplace-ui-assets-core/constants/AssetPageType';
import * as ThemeQueryField from 'marketplace-ui-assets-core/constants/ThemeQueryField';
import { VIEW_ALL_MODULES, VIEW_ALL_THEMES, useAssetSearchBarQuery } from 'marketplace-ui-assets-core/search/hooks/useAssetSearchBarQuery';
import { getAssetBusinessTypeTranslationKey, getAssetPageTypeTranslationKey, getAssetPageFeatureTranslationKey } from 'marketplace-ui-assets-core/utils/i18n';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as OfferingType from 'marketplace-ui-common/offerings/constants/OfferingType';
import InputHighlighter from 'marketplace-ui-common/search/components/InputHighlighter';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'marketplace-ui-common/search/utils';
import { debounce } from 'marketplace-ui-common/utils/debounce';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getComputedQuery, getLocationFromQuery } from '../../../common/utils/assetQuery';
import { useOfferingType } from '../../../context/OfferingTypeContext';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import { getIsAdminPreview, getIsPublicApp } from '../../../selectors/app';
import { getDetailsRoute, getTemplatesRoute } from '../../../utils/urls';
import MobileSearchBar from './MobileSearchBar';

// @ts-expect-error import is untyped
import { useEmbedContext } from '../../../hooks/useEmbedContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CACHED_SEARCH_VALUES_KEY = 'marketplace-assets:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);
const getFullFilterGroup = (filterTypes, getTranslationKey, filterKey, message) => {
  const options = filterTypes.map(filterType => {
    const translatedText = I18n.text(getTranslationKey(filterType));
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText
      }),
      filterKey,
      text: translatedText,
      value: filterType
    };
  });
  return {
    text: I18n.text(message),
    options
  };
};
const getDisplayedFilterGroup = (renderedOptions, input) => {
  const filteredOptions = renderedOptions.options.filter(option => option.text.toLowerCase().includes(input));
  return Object.assign({}, renderedOptions, {
    options: filteredOptions
  });
};
function StorefrontSearchBar({
  isSticky
}) {
  const {
    isEmbedded,
    name: embedFrameName,
    sendMessage,
    sendBroadcastMessage
  } = useEmbedContext();
  const offeringType = useOfferingType();
  const offeringTypeIsModule = offeringType === OfferingType.MODULE;
  const history = useHistory();
  const location = useLocation();
  const cachedSearchValues = getCachedSearchValues();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const isAdminPreview = useSelector(getIsAdminPreview);
  const isPublicApp = useSelector(getIsPublicApp);
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchPlaceholderKey = unescapedText('marketplace-assets-ui.storefront.header.search.placeholders.ALL');
  const assetQueryFieldOrder = useMemo(() => {
    return [ThemeQueryField.BUSINESS_TYPES, ThemeQueryField.PAGE_TYPES, ThemeQueryField.PAGE_FEATURES];
  }, []);
  const businessTypes = Object.values(AssetBusinessType);
  const filterGroups = useMemo(() => {
    if (!offeringTypeIsModule) {
      return {
        [ThemeQueryField.BUSINESS_TYPES]: getFullFilterGroup(businessTypes, getAssetBusinessTypeTranslationKey, ThemeQueryField.BUSINESS_TYPES, 'marketplace-assets-ui.assets.filters.businessType.title'),
        [ThemeQueryField.PAGE_TYPES]: getFullFilterGroup(Object.values(AssetPageType), getAssetPageTypeTranslationKey, ThemeQueryField.PAGE_TYPES, 'marketplace-assets-ui.assets.filters.pageTypes.title'),
        [ThemeQueryField.PAGE_FEATURES]: getFullFilterGroup(Object.values(AssetPageFeature), getAssetPageFeatureTranslationKey, ThemeQueryField.PAGE_FEATURES, 'marketplace-assets-ui.assets.filters.pageFeatures.title')
      };
    }
    return null;
  }, [businessTypes, offeringTypeIsModule]);
  const computedQuery = useMemo(() => getComputedQuery(offeringType, {
    pageNumber: 0
  }, location), [location, offeringType]);
  const searchTerm = useMemo(() => {
    return computedQuery.search || '';
  }, [computedQuery]);
  const searchInputSize = useMemo(() => {
    if (!isSticky) {
      return 'xl';
    }
    return 'default';
  }, [isSticky]);
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }
    return [{
      text: I18n.text(`marketplace-assets-ui.storefront.header.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const handleAssetQueryChange = useCallback(assetQuery => {
    const newLocation = getLocationFromQuery(assetQuery, location, false);
    history.push(newLocation);
  }, [history, location]);
  const formatDropdownOptions = useCallback((recordData, input) => {
    const formattedOptions = [];
    if ('assetOptions' in recordData) {
      formattedOptions.push(...recordData.assetOptions);
    }
    if (!offeringTypeIsModule && filterGroups) {
      assetQueryFieldOrder.forEach(filterGroupKey => {
        formattedOptions.push(getDisplayedFilterGroup(filterGroups[filterGroupKey], input));
      });
    }
    return formattedOptions;
  }, [assetQueryFieldOrder, filterGroups, offeringTypeIsModule]);
  const fetcher = useAssetSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    grouped: true,
    offeringType
  });
  const loadOptions = useCallback((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      callback(null, {
        options: recentSearchOptions
      });
      return;
    }
    const formatOptions = (resp, searchInput) => formatDropdownOptions({
      assetOptions: resp
    }, searchInput);
    fetcher(input).then(resp => {
      const options = formatOptions(resp, input);
      callback(null, {
        options
      });
    }).catch(() => {
      const options = formatOptions([], input);
      callback(null, {
        options
      });
    });
  }, [recentSearchOptions, formatDropdownOptions, fetcher]);
  const debouncedLoadOptions = useMemo(() => debounce(loadOptions), [loadOptions]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    const updatedComputedQuery = produce(computedQuery, draft => {
      draft.search = value;
    });
    setCachedSearchValue(value, valuesFromCache);
    handleAssetQueryChange(updatedComputedQuery);
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [computedQuery, handleAssetQueryChange, trackInteraction]);
  const trackSelectedOptionInteraction = useCallback((itemType, value) => {
    trackInteraction('select search autofill item', {
      itemType,
      value
    });
  }, [trackInteraction]);

  // TO DO: Set stricter types for option
  const onSelectedOptionChange = useCallback(option => {
    const {
      value,
      cachedSearchValue,
      slug,
      item
    } = option;
    if (value === VIEW_ALL_THEMES || value === VIEW_ALL_MODULES) {
      onSearchSubmit(searchInputValue);
      trackSelectedOptionInteraction('see all results', searchInputValue);
      return;
    }
    if ('filterKey' in option && option.filterKey != null) {
      const {
        filterKey
      } = option;
      // @ts-expect-error This expression is not callable
      const updatedComputedQuery = computedQuery.set(filterKey, value);
      handleAssetQueryChange(updatedComputedQuery);
      trackSelectedOptionInteraction(filterKey, value);
      return;
    }
    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value);
      return;
    }
    if (isEmbedded && embedFrameName === EmbedFrame.CONTENT_CREATOR_STOREFRONT) {
      const messageArgs = [PostMessageType.NAVIGATE_TO_THEME_DETAILS, {
        slug
      }];
      sendBroadcastMessage(...messageArgs, EmbedGroup.CONTENT_CREATOR);
      sendMessage(...messageArgs);
      return;
    }
    if (value === '__VIEW_ALL_ASSETS__') {
      history.push(getTemplatesRoute(slug));
    } else {
      history.push(getDetailsRoute(item.offeringType, slug));
    }
    trackSelectedOptionInteraction('offering', slug);
  }, [computedQuery, embedFrameName, handleAssetQueryChange, history, isEmbedded, onSearchSubmit, searchInputValue, sendBroadcastMessage, sendMessage, trackSelectedOptionInteraction]);
  const onClearSearch = useCallback(() => {
    const updatedComputedQuery = produce(computedQuery, draft => {
      draft.search = null;
    });
    handleAssetQueryChange(updatedComputedQuery);
  }, [computedQuery, handleAssetQueryChange]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQueryView, {
      min: "sm",
      children: /*#__PURE__*/_jsx(SearchBar, {
        disabled: isAdminPreview,
        loadOptions: debouncedLoadOptions,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: event => onSelectedOptionChange(event.target.value),
        onInputValueChange: event => setSearchInputValue(event.target.value),
        inputSize: searchInputSize,
        inputValue: searchTerm,
        onClear: onClearSearch,
        placeholder: searchPlaceholderKey
      })
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: "sm",
      children: /*#__PURE__*/_jsx(MobileSearchBar, {
        loadOptions: debouncedLoadOptions,
        onClearSearch: onClearSearch,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: onSelectedOptionChange,
        placeholder: searchPlaceholderKey,
        searchTerm: searchTerm
      })
    })]
  });
}
export default StorefrontSearchBar;